import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import { TourObj } from "../../../shared/types";

type Props = {
  tour: TourObj;
  onBook: (startDate: string) => void;
  /** Checkout session request in flight — disables the button + shows a spinner. */
  loading?: boolean;
  /** User-facing checkout error message; empty when there's nothing to show. */
  error?: string;
  /** Whether the visitor is logged in — checkout is a protected action. */
  isAuthenticated?: boolean;
  /** Whether the initial auth check has resolved (avoids a logged-out flash). */
  authResolved?: boolean;
};

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * Sticky booking card: start-date select, travelers stepper with a live price
 * breakdown, and a Book button that hands the chosen start date back to the
 * page (which kicks off the existing Stripe checkout session).
 */
const BookingCard = ({
  tour,
  onBook,
  loading = false,
  error = "",
  isAuthenticated = false,
  authResolved = false,
}: Props) => {
  const location = useLocation();
  const dates = tour.startDates ?? [];
  const [startDate, setStartDate] = useState(dates[0] ?? "");
  const [travelers, setTravelers] = useState(1);
  const max = tour.maxGroupSize || 12;
  const ctaClass =
    "flex w-full items-center justify-center rounded-[13px] bg-secondary-400 py-4 text-[16px] font-bold text-white transition-colors hover:bg-ink";

  const total = useMemo(() => tour.price * travelers, [tour.price, travelers]);

  return (
    <aside className="lg:sticky lg:top-[90px]">
      <div className="overflow-hidden rounded-[22px] border border-line-200 bg-white shadow-[0_24px_50px_-26px_rgba(0,0,0,0.35)]">
        <div className="px-6 pb-1.5 pt-6">
          <div className="mb-1 flex items-baseline gap-2">
            <span className="font-serif text-[34px]">{fmt(tour.price)}</span>
            <span className="text-[14px] text-muted-300">/ person</span>
          </div>
          <div className="text-[13px] text-muted">
            ★ {tour.ratingsAverage} · {tour.ratingsQuantity} reviews · Free
            cancellation 30 days out
          </div>
        </div>

        <div className="px-6 pb-6 pt-[18px]">
          {/* Start date */}
          <label className="mb-1.5 block text-[11.5px] font-bold tracking-[0.08em] text-muted-300">
            START DATE
          </label>
          {dates.length ? (
            <select
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mb-3.5 w-full cursor-pointer rounded-xl border border-line bg-field px-4 py-3 text-[14.5px] font-semibold text-ink outline-none"
            >
              {dates.map((d) => (
                <option key={d} value={d}>
                  {new Date(d).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </option>
              ))}
            </select>
          ) : (
            <div className="mb-3.5 rounded-xl border border-line bg-field px-4 py-3 text-[14px] text-muted">
              No upcoming dates available
            </div>
          )}

          {/* Travelers */}
          <label className="mb-1.5 block text-[11.5px] font-bold tracking-[0.08em] text-muted-300">
            TRAVELERS
          </label>
          <div className="mb-[18px] flex items-center justify-between rounded-xl border border-line bg-field px-3.5 py-2.5">
            <span className="text-[14.5px] font-semibold">
              {travelers} traveler{travelers > 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-3.5">
              <button
                onClick={() => setTravelers((t) => Math.max(1, t - 1))}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-line bg-white text-[18px] leading-none text-ink"
                aria-label="Fewer travelers"
              >
                –
              </button>
              <span className="min-w-[14px] text-center text-[15px] font-bold">
                {travelers}
              </span>
              <button
                onClick={() => setTravelers((t) => Math.min(max, t + 1))}
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-line bg-white text-[18px] leading-none text-ink"
                aria-label="More travelers"
              >
                +
              </button>
            </div>
          </div>

          {/* Breakdown */}
          <div className="mb-3.5 flex justify-between border-b border-line-100 pb-3.5 text-[14px] text-muted">
            <span>
              {fmt(tour.price)} × {travelers}
            </span>
            <span>{fmt(total)}</span>
          </div>
          <div className="mb-[18px] flex justify-between text-[17px] font-extrabold">
            <span>Total</span>
            <span>{fmt(total)}</span>
          </div>

          {!authResolved ? (
            // Auth check still in flight — neutral, non-clickable placeholder.
            <button disabled className={`${ctaClass} opacity-50`}>
              Book this expedition
            </button>
          ) : !isAuthenticated ? (
            <NavLink
              to="/login"
              state={{ from: location.pathname }}
              className={`${ctaClass} no-underline`}
            >
              Log in to book
            </NavLink>
          ) : (
            <button
              onClick={() => startDate && onBook(startDate)}
              disabled={!startDate || loading}
              className={`${ctaClass} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {loading ? (
                <PulseLoader size={9} color="#ffffff" />
              ) : (
                "Book this expedition"
              )}
            </button>
          )}
          {error && isAuthenticated ? (
            <div
              role="alert"
              className="mt-3 rounded-xl border border-hard/20 bg-hard/5 px-3.5 py-2.5 text-center text-[13px] font-medium text-hard"
            >
              {error}
            </div>
          ) : (
            <div className="mt-3 text-center text-[12.5px] text-muted-300">
              {authResolved && !isAuthenticated
                ? "Log in to reserve your spot"
                : "You won't be charged yet"}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-center gap-2.5 text-[13px] text-muted">
        <span className="text-[16px]">🛡️</span>Carbon-offset &amp; locally guided
      </div>
    </aside>
  );
};

export default BookingCard;
